import React, { useContext, useEffect, useState } from 'react';
import './Appeal.scss';
import { FormikProvider, useFormik } from 'formik';
import { Form, Input, SubmitButton } from 'formik-antd';
import { Button } from 'antd';
import LoaderSpinner from '../LoaderSpinner/LoaderSpinner';
import { Map, Placemark, YMaps, YMapsApi } from 'react-yandex-maps';
import AppealCategorySelect from './AppealCategorySelect/AppealCategorySelect';
import AppealFiles from './AppealFiles/AppealFiles';
import AppealDescription from './AppealDescription/AppealDescription';
import { Category } from '../../model/Category';
import MapPointer from '../../imgs/mapPoint.svg';
import { MapPosition } from '../../model/MapPosition';
import Constants from '../../constants/constants';
import { AppealForm } from '../../model/form/AppealForm';
import * as Yup from 'yup';
import { Context } from '../../index';

interface AppealProps {
  setActiveTab: (activeTab: string) => void;
}

const Appeal = (props: AppealProps): JSX.Element => {
  const { store } = useContext(Context);
  const [appealCategories, setAppealCategories] = useState<Category[]>([]);
  const [coords, setCoords] = useState<MapPosition | null>(null);
  const [address, setAddress] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [municipality, setMunicipality] = useState<number | null>(null);
  const [mapApi, setMapApi] = useState<YMapsApi | null>(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [mapState, setMapState] = useState({
    center: Constants.mapCenterDefault,
    zoom: Constants.mapZoomDefault,
  });
  const [files, setFiles] = useState<string[]>([]);

  const fileHandler = (iri: string) => {
    setFiles((prevState) => {
      return [...prevState, iri];
    });
  };

  const fileRemoveHandler = (iri: string) => {
    setFiles(files.filter((el) => el !== iri));
  };

  useEffect(() => {
    store.getAppealCategories().then((resp) => {
      if (resp) {
        setAppealCategories(resp);
      }
    });
  }, []);

  const resetStates = (): void => {
    setFiles([]);
    setDescription('');
    setAddress('');
    setCoords(null);
    setMunicipality(null);
  };

  const formik = useFormik({
    initialValues: {
      address: '',
      category: 1,
      description: '',
    },
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: Yup.object({
      category: Yup.number()
        .min(1, 'Выберите категорию')
        .required('Выберите категорию'),
      address: Yup.string().required(
        'Необходимо указать адрес в пределах Республики Крым'
      ),
      description: Yup.string()
        .min(20, 'Не менее 20 символов')
        .max(1000, 'Не более 1000 символов')
        .required('Введите текст сообщения'),
    }),
    onSubmit: (values, actions) => {
      setIsSubmitDisabled(true);
      const userId = store.getUserId();
      handleMapPoint();

      if (coords?.longitude || coords?.latitude) {
        const appeal: AppealForm = {
          address: address,
          longitude: coords?.longitude ?? 0,
          latitude: coords?.latitude ?? 0,
          description: description,
          author: userId,
          category: values.category ?? 0,
          municipality: municipality ?? 0,
          files: files,
        };

        store.setAppeal(appeal).then(() => {
          actions.resetForm();
          resetStates();
          props.setActiveTab('1');
        });

        actions.setSubmitting(false);
        setTimeout(() => {
          setIsSubmitDisabled(false);
        }, 300);
      } else {
        setAddress('');
        actions.setSubmitting(false);
        setIsSubmitDisabled(false);
      }
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAddressChange = (event: any): void => {
    setAddress(event.target.value);
    formik.setFieldValue('address', event.target.value);
    formik.setFieldTouched('address');
  };

  const handleGetApiAddressFailed = () => {
    setAddress('');
    setCoords(null);
    setMunicipality(null);
    setMapState({
      center: Constants.mapCenterDefault,
      zoom: Constants.mapZoomDefault,
    });
    formik.setFieldValue('address', '');
    formik.setFieldTouched('address');
  };

  const handleCoordsChange = (coord: MapPosition): void => {
    setCoords(coord);

    mapApi?.geocode([coord.latitude, coord.longitude]).then((result: any) => {
      const municipalityName = result.geoObjects
        .get(0)
        .getAdministrativeAreas()
        .pop();

      store.getMunicipalities(municipalityName).then((resp) => {
        if (resp && resp[0] !== null && resp[0]?.id) {
          setMunicipality(resp[0].id);
          setAddress(result.geoObjects.get(0).getAddressLine());
          setMapState({
            center: [coord.latitude, coord.longitude],
            zoom: Constants.mapZoomAppeal,
          });
          formik.setFieldValue(
            'address',
            result.geoObjects.get(0).getAddressLine()
          );
          formik.setFieldTouched('address');
        } else {
          handleGetApiAddressFailed();
        }
      });
    });
  };

  const handleMapPoint = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mapApi?.geocode(address).then((result: any): void => {
      try {
        const coordinates: number[] = result.geoObjects
          .get(0)
          .geometry.getCoordinates();
        handleCoordsChange({
          latitude: coordinates[0],
          longitude: coordinates[1],
        } as MapPosition);
      } catch (e) {
        handleGetApiAddressFailed();
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMapClick = (e: any): void => {
    const coordinates: number[] = e.get('coords');
    handleCoordsChange({
      latitude: coordinates[0],
      longitude: coordinates[1],
    } as MapPosition);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlacemarkDrag = (e: any): void => {
    const coordinates: number[] = e.get('target').geometry.getCoordinates();
    handleCoordsChange({
      latitude: coordinates[0],
      longitude: coordinates[1],
    } as MapPosition);
  };

  const handleEditorChange = (data: string) => {
    setDescription(data);
    formik.setFieldValue('description', data);
    formik.setFieldTouched('description');
  };

  return (
    <>
      {!appealCategories.length ? (
        <LoaderSpinner />
      ) : (
        <div className="problemReport">
          <FormikProvider value={formik}>
            <Form>
              <div className="problemReport__row">
                {formik.touched.category && formik.errors.category ? (
                  <div className="errors">{formik.errors.category}</div>
                ) : null}
                <AppealCategorySelect
                  title="Категория"
                  name="category"
                  category={appealCategories}
                />
              </div>
              <div className="problemReport__row problemReport__row__adress">
                {formik.touched.address && formik.errors.address ? (
                  <div className="errors">{formik.errors.address}</div>
                ) : null}
                <div className="problemReport__mapData">
                  <Input
                    name="address"
                    placeholder="Адрес"
                    value={address}
                    onChange={handleAddressChange}
                    onBlur={handleMapPoint}
                  />
                  <button onClick={handleMapPoint} type="button">
                    <img src={MapPointer} alt="pointer" />
                  </button>
                </div>
              </div>
              <div className="problemReport__map">
                <YMaps
                  width="100%"
                  height="100%"
                  enterprise
                  query={{
                    apikey: Constants.mapApiKey,
                    load: 'geocode',
                  }}
                >
                  <Map
                    width="100%"
                    height="100%"
                    modules={['geocode']}
                    state={mapState}
                    onLoad={(ymaps) => setMapApi(ymaps)}
                    onClick={onMapClick}
                  >
                    {coords && (
                      <Placemark
                        geometry={[coords.latitude, coords.longitude]}
                        options={{ draggable: true }}
                        onDragEnd={handlePlacemarkDrag}
                      />
                    )}
                  </Map>
                </YMaps>
              </div>
              <div className="problemReport__files">
                <AppealFiles
                  handler={fileHandler}
                  removeHandler={fileRemoveHandler}
                  multiple={true}
                />
              </div>
              <div>
                {formik.touched.description && formik.errors.description ? (
                  <div className="errors">{formik.errors.description}</div>
                ) : null}
                <AppealDescription
                  data={description}
                  setDescription={handleEditorChange}
                />
              </div>
              <div className="problemReport__info">
                <p>
                  Созданное сообщение не подпадает под рассмотрение в
                  соответствии с требованиями{' '}
                  <a
                    href="http://pravo.gov.ru/proxy/ips/?docbody=&nd=102106413"
                    target="_blank"
                    rel="noreferrer"
                  >
                    59-ФЗ
                  </a>
                </p>
              </div>
              <Button.Group>
                <SubmitButton disabled={isSubmitDisabled}>
                  Отправить сообщение
                </SubmitButton>
              </Button.Group>
            </Form>
          </FormikProvider>
        </div>
      )}
    </>
  );
};

export default Appeal;
